import {
  Building,
  Door,
  Foundation,
  Roof,
  Storey,
  Surface,
  Wall,
  Window,
} from '../models/Construction'
import { PileCap } from '../models/Structural'
import { SceneManager } from '../visualization/sceneManager'

const filterBuildingByIds = (building: Building, ids?: string[]): Building => {
  const filteredBuilding: Building = {
    ...building,
    roofs: [],
    storeys: [],
    stairs: [],
    foundations: [],
  }

  if (!ids || ids.length === 0) {
    return filteredBuilding
  }

  for (const storey of building.storeys) {
    if (ids.includes(storey.id)) {
      filteredBuilding.storeys.push(storey)
      continue
    }

    const filteredStorey: Storey = {
      ...storey,
      rooms: [],
      walls: [],
      selected_shear_walls: [],
      optional_shear_walls: [],
      load_bearing_walls: [],
      non_bearing_walls: [],
      beams: [],
      spans: [],
    }

    for (const room of storey.rooms) {
      if (ids.includes(room.id)) {
        filteredStorey.rooms.push(room)
      }
    }

    for (const wall of storey.walls) {
      const filteredWall: Wall = { ...wall, windows: [], doors: [], studs: [] }

      if (ids.includes(wall.id)) {
        filteredStorey.walls.push(wall)
        continue
      }

      for (const window of wall.windows) {
        if (ids.includes(window.id)) {
          filteredWall.windows = wall.windows
        }

        const filteredWindow: Window = { ...window, headers: [], studs: [] }

        for (const stud of window.studs) {
          if (ids.includes(stud.id)) {
            filteredWindow.studs.push(stud)
          }
        }

        for (const header of window.headers) {
          if (ids.includes(header.id)) {
            filteredWindow.headers.push(header)
          }
        }

        if (filteredWindow.studs.length || filteredWindow.headers.length) {
          filteredWall.windows.push(filteredWindow)
        }
      }

      for (const door of wall.doors) {
        if (ids.includes(door.id)) {
          filteredWall.doors.push(door)
          filteredWall.windows = wall.windows
        }

        const filteredDoor: Door = { ...door, studs: [], headers: [] }

        for (const stud of door.studs) {
          if (ids.includes(stud.id)) {
            filteredDoor.studs.push(stud)
          }
        }

        for (const header of door.headers) {
          if (ids.includes(header.id)) {
            filteredDoor.headers.push(header)
          }
        }

        if (filteredDoor.studs.length || filteredDoor.headers.length) {
          filteredWall.doors.push(filteredDoor)
        }
      }

      for (const stud of wall.studs) {
        if (ids.includes(stud.id)) {
          filteredWall.studs.push(stud)
        }
      }

      if (
        filteredWall.windows.length ||
        filteredWall.doors.length ||
        filteredWall.studs.length
      ) {
        filteredStorey.walls.push(filteredWall)
      }
    }

    for (const beam of storey.beams) {
      if (ids.includes(beam.id)) {
        filteredStorey.beams.push(beam)
      }
    }

    for (const span of storey.spans) {
      const filteredJoists = span.joists.filter((joist) =>
        ids.includes(joist.id),
      )
      if (filteredJoists.length) {
        filteredStorey.spans.push({ id: span.id, joists: filteredJoists })
      }
    }

    if (
      filteredStorey.rooms.length ||
      filteredStorey.walls.length ||
      filteredStorey.beams.length ||
      filteredStorey.spans.length
    ) {
      filteredBuilding.storeys.push(filteredStorey)
    }
  }

  for (const roof of building.roofs) {
    if (ids.includes(roof.id)) {
      filteredBuilding.roofs.push(roof)
      const storey = building.storeys.find((s) => s.id === roof.storey_id)
      if (storey) filteredBuilding.storeys.push(storey)
      continue
    }

    const filteredRoof: Roof = {
      ...roof,
      hips: [],
      ridges: [],
      surfaces: [],
    }

    for (const hip of roof.hips) {
      if (ids.includes(hip.id)) {
        filteredRoof.hips.push(hip)
      }
    }

    for (const ridge of roof.ridges) {
      if (ids.includes(ridge.id)) {
        filteredRoof.ridges.push(ridge)
      }
    }

    for (const surface of roof.surfaces) {
      if (ids.includes(surface.id)) {
        filteredRoof.surfaces.push(surface)
        continue
      }

      const filteredSurface: Surface = {
        ...surface,
        rafters: [],
      }

      for (const rafter of surface.rafters) {
        if (ids.includes(rafter.id)) {
          filteredSurface.rafters.push(rafter)
        }
      }

      if (filteredSurface.rafters.length) {
        filteredRoof.surfaces.push(filteredSurface)
      }
    }

    if (
      filteredRoof.hips.length ||
      filteredRoof.ridges.length ||
      filteredRoof.surfaces.length
    ) {
      filteredBuilding.roofs.push(filteredRoof)
    }
  }

  for (const foundation of building.foundations) {
    if (ids.includes(foundation.id)) {
      filteredBuilding.foundations.push(foundation)
      continue
    }

    const filteredFoundation: Foundation = {
      ...foundation,
      beams: [],
      pile_caps: [],
    }

    for (const beam of foundation.beams) {
      if (ids.includes(beam.id)) {
        filteredFoundation.beams.push(beam)
      }
    }

    for (const pileCap of foundation.pile_caps) {
      if (ids.includes(pileCap.id)) {
        filteredFoundation.pile_caps.push(pileCap)
        continue
      }

      const filteredPileCap: PileCap = {
        ...pileCap,
        piles: [],
      }

      for (const pile of pileCap.piles) {
        if (ids.includes(pile.id)) {
          filteredPileCap.piles.push(pile)
        }
      }

      if (filteredPileCap.piles.length) {
        filteredFoundation.pile_caps.push(filteredPileCap)
      }
    }

    if (
      filteredFoundation.beams.length ||
      filteredFoundation.pile_caps.length
    ) {
      filteredBuilding.foundations.push(filteredFoundation)
    }
  }

  return filteredBuilding
}

const updateBuildingElements = (
  building: Building,
  newData: {
    elementId: string
    propertyName: string
    propertyValue: string
  }[],
) => {
  for (const data of newData) {
    for (
      let storeyIndex = 0;
      storeyIndex < building.storeys.length;
      storeyIndex++
    ) {
      const storey = building.storeys[storeyIndex]

      // Update Wall
      const wallIndex = storey.walls.findIndex(
        (wall) => wall.id === data.elementId,
      )
      if (wallIndex !== -1) {
        storey.walls[wallIndex] = {
          ...storey.walls[wallIndex],
          [data.propertyName]: isNaN(Number(data.propertyValue))
            ? data.propertyValue
            : parseFloat(data.propertyValue),
        }
        continue
      }

      // Update Room
      const roomIndex = storey.rooms.findIndex(
        (room) => room.id === data.elementId,
      )
      if (roomIndex !== -1) {
        storey.rooms[roomIndex] = {
          ...storey.rooms[roomIndex],
          [data.propertyName]: isNaN(Number(data.propertyValue))
            ? data.propertyValue
            : parseFloat(data.propertyValue),
        }
        continue
      }

      // Update Floor (inside Room)
      for (let room of storey.rooms) {
        if (room.floor.id === data.elementId) {
          room.floor = {
            ...room.floor,
            [data.propertyName]: isNaN(Number(data.propertyValue))
              ? data.propertyValue
              : parseFloat(data.propertyValue),
          }
          break
        }
      }

      // Update Door or Window
      for (let wall of storey.walls) {
        const doorIndex = wall.doors.findIndex(
          (door) => door.id === data.elementId,
        )
        if (doorIndex !== -1) {
          wall.doors[doorIndex] = {
            ...wall.doors[doorIndex],
            [data.propertyName]: isNaN(Number(data.propertyValue))
              ? data.propertyValue
              : parseFloat(data.propertyValue),
          }
          break
        }

        const windowIndex = wall.windows.findIndex(
          (window) => window.id === data.elementId,
        )
        if (windowIndex !== -1) {
          wall.windows[windowIndex] = {
            ...wall.windows[windowIndex],
            [data.propertyName]: isNaN(Number(data.propertyValue))
              ? data.propertyValue
              : parseFloat(data.propertyValue),
          }
          break
        }
      }
    }
  }

  return building
}

const getValidationItemName = (id: string | null) => {
  const scene = SceneManager.getInstance().getScene()
  if (scene && id) {
    const mesh = scene.getMeshById(id)
    if (mesh && mesh.metadata && mesh.metadata.type) {
      return `${mesh.metadata.type.toString()} Object`
    }
  }

  return 'Object'
}

export { filterBuildingByIds, updateBuildingElements, getValidationItemName }
